import { useEffect, useRef } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import HintIcon from '@/modules/Survey/components/shared/hint-icon'
import { UPSALE_POTENTIAL_OPTIONS } from '@/modules/Survey/constants'
import {
    CreateSurveyRecordPayload,
    RecordResponse,
    UpsalePotential,
} from '@/modules/Survey/types'
import { useTranslation } from 'react-i18next'

// Uncontrolled Textarea: zero re-renders while typing, supports IME (Vietnamese input)
const ImeTextarea = ({
    value,
    onChange,
    ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    value: string
    onChange: (value: string) => void
}) => {
    const ref = useRef<HTMLTextAreaElement>(null)
    const composingRef = useRef(false)

    // Sync DOM only when external value changes (e.g. data loaded/reset from parent)
    useEffect(() => {
        if (ref.current && !composingRef.current) {
            ref.current.value = value
        }
    }, [value])

    return (
        <Textarea
            {...props}
            ref={ref}
            defaultValue={value}
            onChange={(e) => {
                if (!composingRef.current) onChange(e.target.value)
            }}
            onCompositionStart={() => {
                composingRef.current = true
            }}
            onCompositionEnd={(e) => {
                composingRef.current = false
                onChange(e.currentTarget.value)
            }}
        />
    )
}

const SCORE_CATEGORIES = [
    {
        key: 'score_service_quality' as const,
        noteKey: 'note_service_quality' as const,
        labelKey: 'surveys.column.score_service_quality',
        hintKey: 'surveys.form.hints.score_service_quality',
        placeholderKey: 'surveys.form.descriptions.score_service_quality',
    },
    {
        key: 'score_customer_care' as const,
        noteKey: 'note_customer_care' as const,
        labelKey: 'surveys.column.score_customer_care',
        hintKey: 'surveys.form.hints.score_customer_care',
        placeholderKey: 'surveys.form.descriptions.score_customer_care',
    },
    {
        key: 'score_tech_support' as const,
        noteKey: 'note_tech_support' as const,
        labelKey: 'surveys.column.score_tech_support',
        hintKey: 'surveys.form.hints.score_tech_support',
        placeholderKey: 'surveys.form.descriptions.score_tech_support',
    },
    {
        key: 'score_issue_resolution' as const,
        noteKey: 'note_issue_resolution' as const,
        labelKey: 'surveys.column.score_issue_resolution',
        hintKey: 'surveys.form.hints.score_issue_resolution',
        placeholderKey: 'surveys.form.descriptions.score_issue_resolution',
    },
    {
        key: 'score_referral_willingness' as const,
        noteKey: 'note_referral_willingness' as const,
        labelKey: 'surveys.column.score_referral_willingness',
        hintKey: 'surveys.form.hints.score_referral_willingness',
        placeholderKey: 'surveys.form.descriptions.score_referral_willingness',
    },
] as const

export { SCORE_CATEGORIES }

const STANDALONE_NOTES: {
    field: 'improvement' | 'note_general'
    labelKey: string
    hintKey?: string
    placeholderKey?: string
}[] = [
    {
        field: 'improvement',
        labelKey: 'surveys.form.improvement',
        hintKey: 'surveys.form.hints.improvement',
        placeholderKey: 'surveys.form.descriptions.improvement',
    },
    {
        field: 'note_general',
        labelKey: 'surveys.form.note_general',
    },
]

interface Props {
    customerId: number
    response?: CreateSurveyRecordPayload
    onUpdate: (id: number, patch: Partial<RecordResponse>) => void
}

const SurveyExpandedRowContent = ({
    customerId,
    response,
    onUpdate,
}: Props) => {
    const { t } = useTranslation('surveys')
    const records = response?.records

    return (
        <div className="p-4 space-y-3">
            {/* Score + note per category */}
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b">
                        <th className="text-left pb-1.5 font-medium w-[220px]">
                            {t('surveys.column.category', 'Category')}
                        </th>
                        <th className="text-center pb-1.5 font-medium w-[100px]">
                            {t('surveys.column.score', 'Score')}{' '}
                            <span className="text-red-500">*</span>
                        </th>
                        <th className="text-left pb-1.5 font-medium">
                            {t('surveys.column.note', 'Note')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {SCORE_CATEGORIES.map((cat) => (
                        <tr key={cat.key} className="border-b last:border-0">
                            <td className="py-1.5 pr-2 text-muted-foreground text-xs align-middle">
                                <span className="flex items-center gap-1">
                                    {t(cat.labelKey, cat.key)}
                                    <HintIcon
                                        hint={t(cat.hintKey)}
                                        className="h-3 w-3"
                                    />
                                </span>
                            </td>
                            <td className="py-1.5 px-2 align-middle">
                                <Input
                                    type="number"
                                    min={1}
                                    max={10}
                                    className="h-7 text-center w-16 mx-auto"
                                    value={records?.[cat.key] || ''}
                                    onChange={(e) => {
                                        const v = parseInt(e.target.value, 10)
                                        onUpdate(customerId, {
                                            [cat.key]: isNaN(v)
                                                ? 0
                                                : Math.max(1, Math.min(10, v)),
                                        })
                                    }}
                                />
                            </td>
                            <td className="py-1.5 align-middle">
                                <ImeTextarea
                                    className="h-7 py-1"
                                    placeholder={t(cat.placeholderKey)}
                                    value={records?.[cat.noteKey] || ''}
                                    onChange={(v) =>
                                        onUpdate(customerId, {
                                            [cat.noteKey]: v,
                                        })
                                    }
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Upsale potential */}
            <div className="flex items-center gap-3 flex-wrap pt-3 border-t">
                <span className="text-xs font-medium text-muted-foreground shrink-0 flex items-center gap-1">
                    {t('surveys.column.upsale_potential', 'Upsale')}
                    <HintIcon
                        hint={t('surveys.form.hints.upsale_potential')}
                        className="h-3 w-3"
                    />
                </span>
                {UPSALE_POTENTIAL_OPTIONS.map((option) => (
                    <label
                        key={option.value}
                        className="flex items-center gap-1.5 cursor-pointer"
                    >
                        <Checkbox
                            className="self-center"
                            checked={(records?.upsale_potential || []).includes(
                                option.value
                            )}
                            onCheckedChange={(checked) => {
                                const current = records?.upsale_potential || []
                                onUpdate(customerId, {
                                    upsale_potential: checked
                                        ? [
                                              ...current,
                                              option.value as UpsalePotential,
                                          ]
                                        : current.filter(
                                              (v) => v !== option.value
                                          ),
                                })
                            }}
                        />
                        <span className="text-xs leading-none">
                            {option.label}
                        </span>
                    </label>
                ))}
            </div>

            {/* Standalone notes */}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t items-start">
                {STANDALONE_NOTES.map(
                    ({ field, labelKey, hintKey, placeholderKey }) => (
                        <div key={field} className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                {t(labelKey, field)}
                                {hintKey && (
                                    <HintIcon
                                        hint={t(hintKey)}
                                        className="h-3 w-3"
                                    />
                                )}
                            </label>
                            <ImeTextarea
                                className="text-xs"
                                placeholder={t(
                                    placeholderKey ||
                                        'surveys.form.note_placeholder',
                                    'Note...'
                                )}
                                value={records?.[field] || ''}
                                onChange={(v) =>
                                    onUpdate(customerId, { [field]: v })
                                }
                            />
                        </div>
                    )
                )}
            </div>
        </div>
    )
}

export default SurveyExpandedRowContent
